/**
 * Studio project rooms: projects, captures, architecture snapshots.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  collectionGroup,
  doc,
  setDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot,
  where,
  getDocs,
  limit,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  DEFAULT_STUDIO_PROJECT_ID,
  STUDIO_SUBCOLLECTION_PLACEHOLDER_ID,
  isStudioPlaceholderDoc,
  ownerUidFromStudioProjectRef,
  normalizedProjectId,
} from '../constants/studioProjects';
import {
  getDefaultProjectSettings,
  sanitizeProjectTags,
} from '../constants/projectTypes';
import { filterRealCaptureDocs } from '../constants/captureCollection';
import {
  membershipMirrorSettingsCollection,
  membershipDocsFromSettingsSnapshot,
} from '../utils/membershipMirrorFirestore';
import { ROLLING_CAPTURE_PERIOD_MS } from '../constants/uploadLimits';
import firebaseErrorMessage from '../utils/firebaseErrorMessage';
import { normalizeProjectMemberRole } from '../constants/projectMemberRoles';
import { sortTimelineSnapshots } from '../utils/studioTimeline';
import { thumbnailStoragePathOrUrlToMediaUrl } from '../utils/demoThumbnails';
import {
  TIMELINE_DATA_SUB,
  TIMELINE_INFO_KIND,
  TIMELINE_ASSETS_SUB_LEGACY,
  SNAPSHOTS_SUB_LEGACY,
} from '../constants/firestoreCollections';
import { timelineInfoToSnapshotStep, isTimelineFileDocId } from '../utils/timelineDataPaths';
import { getStudioQuotaCache } from './studioMetrics/studioQuotaCache';
import { computeStudioQuotaCounts } from './studioMetrics/studioMetricsComputations';

const STUDIO_COLLECTION = 'studio';
const CAPTURES_SUB = 'captures';

const MAP_TILESET_STATUSES = new Set(['pending', 'processing', 'ready', 'failed']);

/** Ensures map-tab fields from Firestore are in the shape StudioMapTab expects. */
function studioCaptureFromDoc(docSnap) {
  const data = docSnap.data();
  const tilesetUrl =
    typeof data.tilesetUrl === 'string' && data.tilesetUrl.trim() ? data.tilesetUrl.trim() : null;
  const rawStatus = data.tilesetStatus;
  const tilesetStatus = MAP_TILESET_STATUSES.has(rawStatus) ? rawStatus : null;
  return {
    id: docSnap.id,
    ...data,
    tilesetUrl,
    tilesetStatus,
  };
}
const SNAPSHOTS_SUB = SNAPSHOTS_SUB_LEGACY;
const TIMELINE_ASSETS_SUB = TIMELINE_DATA_SUB;

/** True only for captures actively consuming a quota slot (not failed/cancelled). */
export function captureCountsAsInFlight(data) {
  if (!data) return false;
  const processing = data.processing;
  if (!processing) return false;
  const status = String(processing.status ?? '').toLowerCase();
  if (status === 'completed' || status === 'failed' || status === 'cancelled') return false;
  const failedAt = processing.failedAt;
  if (failedAt != null && String(failedAt).trim() !== '') return false;
  return status === 'processing' || status === 'queued';
}

/** Sum in-flight captures across all studio projects (free-plan limit checks). */
export async function countProcessingCapturesAcrossStudio(uid) {
  if (!uid) return 0;
  const cached = getStudioQuotaCache(uid);
  if (cached) return cached.processingCount;
  const studioSnap = await getDocs(collection(db, 'users', uid, STUDIO_COLLECTION));
  let n = 0;
  for (const p of studioSnap.docs) {
    if (isStudioPlaceholderDoc(p)) continue;
    const capSnap = await getDocs(
      query(
        collection(db, 'users', uid, STUDIO_COLLECTION, p.id, CAPTURES_SUB),
        where('processing.status', 'in', ['processing', 'queued'])
      )
    );
    for (const d of filterRealCaptureDocs(capSnap.docs)) {
      if (captureCountsAsInFlight(d.data())) n += 1;
    }
  }
  return n;
}

/**
 * Lifetime count of captures with processing.status === "completed" under all studio projects.
 * Matches free-tier enforcement semantics (same as billing metrics increment on completion).
 * Used to cross-check users/{uid}/billing/metrics so the UI cannot under-count via stale metrics.
 */
export async function countCompletedCapturesAcrossStudio(uid) {
  if (!uid) return 0;
  const cached = getStudioQuotaCache(uid);
  if (cached) return cached.completedCount;
  const studioSnap = await getDocs(collection(db, 'users', uid, STUDIO_COLLECTION));
  let n = 0;
  for (const p of studioSnap.docs) {
    if (isStudioPlaceholderDoc(p)) continue;
    const capSnap = await getDocs(collection(db, 'users', uid, STUDIO_COLLECTION, p.id, CAPTURES_SUB));
    for (const d of filterRealCaptureDocs(capSnap.docs)) {
      const st = d.data()?.processing?.status;
      if (st === 'completed') n += 1;
    }
  }
  return n;
}

/**
 * Completed captures in the rolling window (billing period proxy for Pro / Studio monthly caps).
 * @param {string} uid
 * @param {number} [periodMs] Rolling window length (default 30d).
 * @param {number|null|undefined} [planStartedAtMs] When set, counts only captures at/after max(planStartedAtMs, now - periodMs).
 */
export async function countCompletedCapturesInRollingWindow(
  uid,
  periodMs = ROLLING_CAPTURE_PERIOD_MS,
  planStartedAtMs = null
) {
  if (!uid) return 0;
  const cached = getStudioQuotaCache(uid);
  if (cached?.captures?.length != null) {
    return computeStudioQuotaCounts(cached.captures, { periodMs, planStartedAtMs })
      .rollingCompletedCount;
  }
  const windowStart = Date.now() - periodMs;
  const cutoffMs =
    planStartedAtMs != null && Number.isFinite(Number(planStartedAtMs))
      ? Math.max(Number(planStartedAtMs), windowStart)
      : windowStart;
  const studioSnap = await getDocs(collection(db, 'users', uid, STUDIO_COLLECTION));
  let n = 0;
  for (const p of studioSnap.docs) {
    if (isStudioPlaceholderDoc(p)) continue;
    const capSnap = await getDocs(collection(db, 'users', uid, STUDIO_COLLECTION, p.id, CAPTURES_SUB));
    for (const d of filterRealCaptureDocs(capSnap.docs)) {
      const data = d.data();
      if (data?.processing?.status !== 'completed') continue;
      const created = data?.createdAt;
      const t = created?.toMillis?.() ?? 0;
      if (t >= cutoffMs) n += 1;
    }
  }
  return n;
}

/**
 * New projects are private by default — owner only until per-project invite.
 * @param {string} ownerUid
 */
async function fetchWorkspaceMemberSeed(ownerUid) {
  return {
    memberIds: [ownerUid],
    memberRoles: { [ownerUid]: 'owner' },
    authorizedUsers: [{ uid: ownerUid, role: 'owner' }],
  };
}

/**
 * Create a new project folder under users/{uid}/studio/{newId}.
 * @param {string} uid
 * @param {string} projectName
 * @param {unknown} [projectTags] — normalized via {@link sanitizeProjectTags}
 * @returns {Promise<string>} New project document id
 */
export async function createStudioProject(uid, projectName, projectTags) {
  if (!uid || !String(projectName || '').trim()) {
    throw new Error('Project name is required');
  }
  const colRef = collection(db, 'users', uid, STUDIO_COLLECTION);
  const ref = doc(colRef);
  const defaults = getDefaultProjectSettings();
  const tags = sanitizeProjectTags(projectTags);
  const { memberIds, memberRoles, authorizedUsers } = await fetchWorkspaceMemberSeed(uid);
  await setDoc(ref, {
    projectName: String(projectName).trim(),
    ...defaults,
    projectTags: tags,
    locationMetadata: null,
    authorizedUsers,
    memberIds,
    memberRoles,
    captureCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * @returns {{
 *   projects: Array<object>,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export function useProjectsList(userId, options = {}) {
  const retryNonce = typeof options.retryNonce === 'number' ? options.retryNonce : 0;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(!!userId);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setProjects([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const colRef = collection(db, 'users', userId, STUDIO_COLLECTION);
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      try {
        if (auth.currentUser?.uid === userId) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;
      unsub = onSnapshot(
        colRef,
        (snap) => {
          const list = snap.docs
            .filter((d) => !isStudioPlaceholderDoc(d))
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const ta = (a.updatedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0);
              const tb = (b.updatedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0);
              return tb - ta;
            });
          setProjects(list);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('useProjectsList:', err);
          setError(firebaseErrorMessage(err, 'Failed to load projects'));
          setProjects([]);
          setLoading(false);
        }
      );
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [userId, retryNonce]);

  return { projects, loading, error };
}

/**
 * @returns {{ captures: Array<object>, loading: boolean, error: string | null }}
 */
export function useProjectCaptures(userId, projectId, options = {}) {
  const enabled = options.enabled !== false && !!projectId;
  /** Increment from the host to force a fresh onSnapshot after recoverable errors. */
  const retryNonce = typeof options.retryNonce === 'number' ? options.retryNonce : 0;
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(!!userId && enabled);
  const [error, setError] = useState(null);

  const pid = normalizedProjectId(projectId);

  useEffect(() => {
    if (!enabled || !userId || !projectId) {
      setCaptures([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const qRef = query(
      collection(db, 'users', userId, STUDIO_COLLECTION, pid, CAPTURES_SUB),
      orderBy('createdAt', 'desc')
    );
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      try {
        if (auth.currentUser?.uid === userId) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;
      unsub = onSnapshot(
        qRef,
        (snap) => {
          const list = filterRealCaptureDocs(snap.docs).map((d) => studioCaptureFromDoc(d));
          setCaptures(list);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('useProjectCaptures:', err);
          setError(firebaseErrorMessage(err, 'Failed to load captures'));
          setCaptures([]);
          setLoading(false);
        }
      );
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [userId, pid, projectId, enabled, retryNonce]);

  return { captures, loading, error };
}

/**
 * Up to four most recent captures' thumbnail URLs for project folder preview (collage).
 * @param {string} userId
 * @param {string} projectId
 * @param {{ enabled?: boolean }} [options]
 * @returns {{ thumbnails: Array<string | null>, loading: boolean, error: string | null }}
 */
export function useProjectPreviewThumbnails(userId, projectId, options = {}) {
  const enabled = options.enabled !== false && !!userId && !!projectId;
  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);

  const pid = normalizedProjectId(projectId);

  useEffect(() => {
    if (!enabled || !userId || !projectId) {
      setThumbnails([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const qRef = query(
      collection(db, 'users', userId, STUDIO_COLLECTION, pid, CAPTURES_SUB),
      orderBy('createdAt', 'desc'),
      limit(4)
    );
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      try {
        if (auth.currentUser?.uid === userId) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;
      unsub = onSnapshot(
        qRef,
        (snap) => {
          const list = filterRealCaptureDocs(snap.docs).map((d) => {
            const data = d.data();
            const raw = data.storage?.thumbnailPath ?? data.thumbnailPath;
            if (raw == null || typeof raw !== 'string') return null;
            const t = raw.trim();
            if (!t) return null;
            return thumbnailStoragePathOrUrlToMediaUrl(t);
          });
          setThumbnails(list);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('useProjectPreviewThumbnails:', err);
          setError(firebaseErrorMessage(err, 'Failed to load preview thumbnails'));
          setThumbnails([]);
          setLoading(false);
        }
      );
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [userId, pid, projectId, enabled]);

  return { thumbnails, loading, error };
}

/**
 * Architecture timeline steps from captures/{captureId}/timelineData/_info (collection group).
 * Falls back to legacy project snapshots during migration.
 * @returns {{ snapshots: Array<object>, loading: boolean }}
 */
export function useProjectSnapshots(userId, projectId, options = {}) {
  const enabled = options.enabled !== false && !!projectId;
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(!!enabled);

  const pid = normalizedProjectId(projectId);

  useEffect(() => {
    if (!enabled || !userId || !projectId) {
      setSnapshots([]);
      return undefined;
    }
    setLoading(true);
    const infoQ = query(
      collectionGroup(db, TIMELINE_DATA_SUB),
      where('ownerUid', '==', userId),
      where('projectId', '==', pid),
      where('kind', '==', TIMELINE_INFO_KIND),
    );
    const legacyQ = query(
      collection(db, 'users', userId, STUDIO_COLLECTION, pid, SNAPSHOTS_SUB),
      orderBy('createdAt', 'asc'),
    );
    let cancelled = false;
    let infoSteps = [];
    let legacySteps = [];
    let infoReady = false;
    let legacyReady = false;

    const publish = () => {
      if (!infoReady || !legacyReady) return;
      const raw =
        infoSteps.length > 0
          ? infoSteps
          : legacySteps.map((d) => ({ ...d.data(), id: d.id }));
      setSnapshots(sortTimelineSnapshots(raw));
      setLoading(false);
    };

    let unsubInfo = () => {};
    let unsubLegacy = () => {};
    (async () => {
      try {
        if (auth.currentUser?.uid === userId) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;
      unsubInfo = onSnapshot(
        infoQ,
        (snap) => {
          infoSteps = snap.docs
            .map((d) => timelineInfoToSnapshotStep({ ...d.data(), id: d.data()?.captureId || d.id }))
            .filter(Boolean);
          infoReady = true;
          publish();
        },
        (e) => {
          console.warn('useProjectSnapshots timelineData:', e);
          infoSteps = [];
          infoReady = true;
          publish();
        },
      );
      unsubLegacy = onSnapshot(
        legacyQ,
        (snap) => {
          legacySteps = snap.docs;
          legacyReady = true;
          publish();
        },
        (e) => {
          console.warn('useProjectSnapshots legacy snapshots:', e);
          legacySteps = [];
          legacyReady = true;
          publish();
        },
      );
    })();
    return () => {
      cancelled = true;
      unsubInfo();
      unsubLegacy();
    };
  }, [userId, pid, projectId, enabled]);

  return { snapshots, loading };
}

/**
 * Per-capture timeline file attachments (PDF / images) under timelineData (excludes _info doc).
 * @returns {{ assets: Array<object>, loading: boolean }}
 */
export function useCaptureTimelineAssets(userId, projectId, captureId, options = {}) {
  const enabled = options.enabled !== false && !!projectId && !!captureId;
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  const pid = normalizedProjectId(projectId);

  useEffect(() => {
    if (!enabled || !userId || !captureId) {
      setAssets([]);
      return undefined;
    }
    setLoading(true);
    const colRef = collection(
      db,
      'users',
      userId,
      STUDIO_COLLECTION,
      pid,
      CAPTURES_SUB,
      captureId,
      TIMELINE_ASSETS_SUB,
    );
    const legacyColRef = collection(
      db,
      'users',
      userId,
      STUDIO_COLLECTION,
      pid,
      CAPTURES_SUB,
      captureId,
      TIMELINE_ASSETS_SUB_LEGACY,
    );
    let cancelled = false;
    let unsub = () => {};
    let unsubLegacy = () => {};
    let primary = [];
    let legacy = [];
    let primaryReady = false;
    let legacyReady = false;
    const publish = () => {
      if (!primaryReady || !legacyReady) return;
      const raw = (primary.length > 0 ? primary : legacy).filter((d) =>
        isTimelineFileDocId(d.id),
      );
      raw.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
      setAssets(raw);
      setLoading(false);
    };
    (async () => {
      try {
        if (auth.currentUser?.uid === userId) {
          await auth.currentUser.getIdToken();
        }
      } catch (_) {}
      if (cancelled) return;
      unsub = onSnapshot(
        colRef,
        (snap) => {
          primary = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          primaryReady = true;
          publish();
        },
        () => {
          primary = [];
          primaryReady = true;
          publish();
        },
      );
      unsubLegacy = onSnapshot(
        legacyColRef,
        (snap) => {
          legacy = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          legacyReady = true;
          publish();
        },
        () => {
          legacy = [];
          legacyReady = true;
          publish();
        },
      );
    })();
    return () => {
      cancelled = true;
      unsub();
      unsubLegacy();
    };
  }, [userId, pid, projectId, captureId, enabled]);

  return { assets, loading };
}

/**
 * Combined hook for studio page: folder/capture mode (no auto-created default project).
 */
export function useStudioProjects(userId, options = {}) {
  const { projects, loading: projectsLoading, error: projectsError } = useProjectsList(userId, options);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  useEffect(() => {
    setSelectedProjectId(null);
  }, [userId]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );

  return {
    projects,
    projectsLoading,
    projectsError,
    selectedProjectId,
    setSelectedProjectId,
    selectedProject,
    studioCollectionPath: userId ? ['users', userId, STUDIO_COLLECTION] : null,
  };
}

/**
 * Projects shared with the signed-in user.
 * Primary: collection group on studio where memberIds contains uid.
 * Fallback: users/{uid}/settings/{ownerUid} collaborator mirrors (+ legacy memberships subcollection) and direct project doc listeners.
 * @returns {{ sharedProjects: Array<object & { ownerUid: string }>, loading: boolean, error: string | null }}
 */
function projectIdsFromMembershipData(data) {
  if (!data || typeof data !== 'object') return [];
  const ids = [];
  for (const [key, val] of Object.entries(data)) {
    if (key.startsWith('projects.') && val === true) {
      const pid = key.slice('projects.'.length).trim();
      if (pid) ids.push(pid);
    }
  }
  return ids;
}

function sharedProjectFromDoc(projSnap, myUid) {
  if (isStudioPlaceholderDoc(projSnap)) return null;
  const ownerUid = ownerUidFromStudioProjectRef(projSnap.ref);
  if (!ownerUid || ownerUid === myUid) return null;
  const data = projSnap.data() || {};
  const memberRoles = data.memberRoles && typeof data.memberRoles === 'object' ? data.memberRoles : {};
  return {
    id: projSnap.id,
    ownerUid,
    ...data,
    viewerRole: normalizeProjectMemberRole(memberRoles[myUid]),
  };
}

function sortSharedProjects(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.updatedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
    const tb = b.updatedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
}

export function useSharedProjects(myUid, options = {}) {
  const retryNonce = typeof options.retryNonce === 'number' ? options.retryNonce : 0;
  const [sharedProjects, setSharedProjects] = useState([]);
  const [loading, setLoading] = useState(!!myUid);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!myUid) {
      setSharedProjects([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    let cancelled = false;

    const merged = new Map();
    const projectUnsubs = new Map();
    let collectionGroupReady = false;
    let membershipsReady = false;
    let collectionGroupError = null;
    let membershipError = null;

    const publish = () => {
      if (cancelled) return;
      const rows = sortSharedProjects(Array.from(merged.values()));
      setSharedProjects(rows);
      const bothDone = collectionGroupReady && membershipsReady;
      setLoading(!bothDone);
      const errMsg = collectionGroupError && membershipError && rows.length === 0
        ? firebaseErrorMessage(collectionGroupError, 'Failed to load shared projects')
        : null;
      setError(errMsg);
    };

    const upsertRow = (row) => {
      if (!row?.id || !row?.ownerUid) return;
      merged.set(`${row.ownerUid}:${row.id}`, row);
      publish();
    };

    const removeProjectKey = (key) => {
      if (merged.delete(key)) publish();
    };

    const projectDocNextHandler = (key) => (projSnap) => {
      if (cancelled) return;
      if (!projSnap.exists?.()) {
        removeProjectKey(key);
        return;
      }
      const row = sharedProjectFromDoc(projSnap, myUid);
      if (row) upsertRow(row);
      else removeProjectKey(key);
    };

    const projectDocErrorHandler = (ownerUid, projectId, key) => (err) => {
      if (cancelled) return;
      console.error('useSharedProjects membership project:', ownerUid, projectId, err);
      membershipError = err;
      removeProjectKey(key);
    };

    const syncMembershipProjectListeners = (membershipDocs) => {
      const desiredKeys = new Set();

      for (const memSnap of membershipDocs) {
        const ownerUid = memSnap.id;
        if (!ownerUid || ownerUid === myUid) continue;
        for (const projectId of projectIdsFromMembershipData(memSnap.data())) {
          const key = `${ownerUid}:${projectId}`;
          desiredKeys.add(key);
          if (projectUnsubs.has(key)) continue;

          const ref = doc(db, 'users', ownerUid, STUDIO_COLLECTION, projectId);
          const unsubProject = onSnapshot(
            ref,
            projectDocNextHandler(key),
            projectDocErrorHandler(ownerUid, projectId, key),
          );
          projectUnsubs.set(key, unsubProject);
        }
      }

      for (const [key, unsubProject] of projectUnsubs.entries()) {
        if (!desiredKeys.has(key)) {
          unsubProject();
          projectUnsubs.delete(key);
          removeProjectKey(key);
        }
      }
    };

    let unsubCollectionGroup = () => {};
    let unsubMemberships = () => {};

    (async () => {
      try {
        if (auth.currentUser?.uid === myUid) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;

      const sharedQuery = query(
        collectionGroup(db, STUDIO_COLLECTION),
        where('memberIds', 'array-contains', myUid),
      );

      unsubCollectionGroup = onSnapshot(
        sharedQuery,
        (snap) => {
          if (cancelled) return;
          collectionGroupError = null;
          for (const projSnap of snap.docs) {
            const row = sharedProjectFromDoc(projSnap, myUid);
            if (row) upsertRow(row);
          }
          collectionGroupReady = true;
          publish();
        },
        (err) => {
          if (cancelled) return;
          console.error('useSharedProjects collection group:', err);
          collectionGroupError = err;
          collectionGroupReady = true;
          publish();
        },
      );

      const mergeMembershipDocs = (settingsDocs, legacyDocs) => {
        const byOwner = new Map();
        for (const d of settingsDocs) byOwner.set(d.id, d);
        for (const d of legacyDocs) {
          if (!byOwner.has(d.id)) byOwner.set(d.id, d);
        }
        return Array.from(byOwner.values());
      };

      let settingsMembershipDocs = [];
      let legacyMembershipDocs = [];

      const publishMemberships = () => {
        if (cancelled) return;
        membershipError = null;
        syncMembershipProjectListeners(mergeMembershipDocs(settingsMembershipDocs, legacyMembershipDocs));
        membershipsReady = true;
        publish();
      };

      const unsubMemSettings = onSnapshot(
        membershipMirrorSettingsCollection(db, myUid),
        (snap) => {
          settingsMembershipDocs = membershipDocsFromSettingsSnapshot(snap);
          publishMemberships();
        },
        (err) => {
          if (cancelled) return;
          console.error('useSharedProjects settings memberships:', err);
          membershipError = err;
          membershipsReady = true;
          publish();
        },
      );

      const unsubMemLegacy = onSnapshot(
        collection(db, 'users', myUid, 'memberships'),
        (snap) => {
          legacyMembershipDocs = snap.docs;
          publishMemberships();
        },
        (err) => {
          if (cancelled) return;
          console.error('useSharedProjects legacy memberships:', err);
          membershipError = err;
          membershipsReady = true;
          publish();
        },
      );

      unsubMemberships = () => {
        unsubMemSettings();
        unsubMemLegacy();
      };
    })();

    return () => {
      cancelled = true;
      unsubCollectionGroup();
      unsubMemberships();
      for (const unsubProject of projectUnsubs.values()) unsubProject();
      projectUnsubs.clear();
    };
  }, [myUid, retryNonce]);

  return { sharedProjects, loading, error };
}

/**
 * Single project doc listener (shared routes — avoids listing entire owner studio).
 * @returns {{ project: object | null, loading: boolean, error: string | null }}
 */
export function useSingleStudioProject(ownerUid, projectId, options = {}) {
  const enabled = options.enabled !== false && !!ownerUid && !!projectId;
  const retryNonce = typeof options.retryNonce === 'number' ? options.retryNonce : 0;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);
  const pid = normalizedProjectId(projectId);

  useEffect(() => {
    if (!enabled || !ownerUid || !projectId) {
      setProject(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const ref = doc(db, 'users', ownerUid, STUDIO_COLLECTION, pid);
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      try {
        if (auth.currentUser?.uid) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;
      unsub = onSnapshot(
        ref,
        (snap) => {
          if (cancelled) return;
          if (snap.exists() && !isStudioPlaceholderDoc(snap)) {
            setProject({ id: snap.id, ...snap.data() });
          } else {
            setProject(null);
          }
          setLoading(false);
          setError(null);
        },
        (err) => {
          if (cancelled) return;
          console.error('useSingleStudioProject:', err);
          setError(firebaseErrorMessage(err, 'Failed to load project'));
          setProject(null);
          setLoading(false);
        },
      );
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [ownerUid, pid, projectId, enabled, retryNonce]);

  return { project, loading, error };
}

/**
 * Pending per-project email invites (owner reads projectInvitesPending subcollection).
 */
export function useProjectPendingInvites(ownerUid, projectId, options = {}) {
  const enabled = options.enabled !== false && !!ownerUid && !!projectId;
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(!!enabled);
  const pid = normalizedProjectId(projectId);

  useEffect(() => {
    if (!enabled) {
      setInvites([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const colRef = collection(db, 'users', ownerUid, STUDIO_COLLECTION, pid, 'projectInvitesPending');
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        setInvites(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() || {}) }))
            .filter((r) => r.status === 'pending'),
        );
        setLoading(false);
      },
      () => {
        setInvites([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [ownerUid, pid, projectId, enabled]);

  return { invites, loading };
}

export { DEFAULT_STUDIO_PROJECT_ID, STUDIO_SUBCOLLECTION_PLACEHOLDER_ID };
